import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandWordmark } from "@/features/landing/BrandWordmark";
import { LogoMark } from "@/features/landing/Navbar";
import "@/features/landing/landing.css";

/**
 * Shared chrome for every sign-in page — Scheda's own front door.
 *
 * <h2>Why it looks like the marketing site</h2>
 * This is the first page a visitor sees after clicking "Log in" on
 * delgentic.com, and the whole point of moving off Cognito's hosted pages was
 * that the journey stopped looking like one product. So it reuses the landing's
 * actual assets — {@code landing.css}, the {@code .landing-bg} grid + glow, the
 * gradient {@code .cta} pill, the brand lockup — rather than a lookalike that
 * would drift the first time the brand moves.
 *
 * <p>The nav is deliberately reduced to the lockup and the language switcher:
 * the landing's nav links point at marketing anchors ({@code #how},
 * {@code #contact}) that don't exist here, and a page asking for a password
 * should offer one obvious action.</p>
 *
 * <p><b>No marketing copy.</b> This page carries only the form for now; the
 * {@code aside} slot below is where Scheda-specific content lands when there is
 * some to say. Keeping the slot empty is intentional — better a quiet page than
 * borrowed copy about the platform.</p>
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  aside,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Below-card line, e.g. "New here? Create an account". */
  footer?: ReactNode;
  /** Reserved for future Scheda content beside the card. Empty today. */
  aside?: ReactNode;
}) {
  return (
    <div className="landing min-h-screen">
      <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4">
        <nav className="nav-capsule flex w-full max-w-5xl items-center justify-between rounded-full px-3 py-2">
          <Link to="/signin" className="flex items-center gap-2 pl-1 pr-2">
            <LogoMark />
            <BrandWordmark />
          </Link>
          <LanguageSwitcher />
        </nav>
      </header>

      <main className="relative">
        <div className="landing-bg" aria-hidden="true" />
        <div
          className={
            aside
              ? "relative mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-32 lg:grid-cols-[1fr_minmax(380px,440px)]"
              : "relative mx-auto flex max-w-md flex-col px-6 pb-20 pt-32"
          }
        >
          {aside}
          <section className="w-full">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-7 shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] backdrop-blur sm:p-8">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{subtitle}</p>
              )}
              <div className="mt-6">{children}</div>
            </div>
            {footer && (
              <p className="mt-5 text-center text-sm text-slate-600">{footer}</p>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
